import React, { useState, useEffect, useCallback } from 'react';
import { getReportPreview } from '@/api/scrapbook-data';
import XLight from '@/assets/xLight.svg';
import ArrowRight from '@/assets/arrowRightLight.svg';
import { HeadingTag, Modal } from '@/components';
import AtAGlance from '@/components/report/ataglance/AtAGlance';
import HeroData from '@/modules/ReportHero/HeroData';
import { useTranslations } from 'next-intl';

interface ReportPreviewProps {
  reportId: string;
  isOpen: boolean;
  onClose: () => void;
}

const ReportPreview: React.FC<ReportPreviewProps> = ({ reportId, isOpen, onClose }) => {
  const [reportDetails, setReportDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(1440);
  const t = useTranslations();

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    if (typeof window !== 'undefined') {
      setWindowWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const fetchReportData = useCallback(async () => {
    if (windowWidth <= 600) return;

    setLoading(true);
    try {
      const data = await getReportPreview(reportId);
      setReportDetails(data);
    } catch (error) {
      console.error('Failed to fetch report preview:', error);
    } finally {
      setLoading(false);
    }
  }, [reportId, windowWidth]);

  useEffect(() => {
    if (reportId && isOpen) {
      fetchReportData();
    }
  }, [reportId, isOpen, fetchReportData]);

  const handleViewReport = () => {
    if (reportDetails?.link?.href) {
      window.open(reportDetails.link.href, '_blank');
    }
  };

  const handleClose = () => {
    setReportDetails(null);
    onClose();
  };

  if (windowWidth <= 600) return null;

  return (
    <Modal open={isOpen} onClose={handleClose} contanerClassName="h-4/5 w-4/5 bg-white z-[60]">
      <div className="theme--light flex h-full flex-col">
        {/* Header */}
        <div className="v-card__title flex-shrink-0 rounded-t-lg bg-white text-black">
          <div className="elevation-0 flex w-full items-center">
            <div>
              <span className="font-sohne hidden text-xl md:block">Report Preview</span>
            </div>
            <div className="spacer flex-1"></div>
            <button type="button" className="v-btn v-btn--icon" onClick={handleClose}>
              <XLight height="28" width="28" />
            </button>
          </div>
        </div>

        <hr role="separator" aria-orientation="horizontal" className="v-divider theme--light" />

        {/* Content */}
        <div
          className="v-card__text h-full max-h-screen flex-1 overflow-y-auto bg-white px-0 text-black sm:h-[60vh] sm:px-6"
          data-lenis-prevent
        >
          {loading ? (
            <div className="flex h-64 items-center justify-center"></div>
          ) : reportDetails ? (
            <div className="module-base-dark container mt-7 hidden sm:block">
              <div className="flex justify-center">
                <div className="w-full">
                  <HeadingTag animate={false} tag="h2" heading={reportDetails.reportTitle} />
                </div>
              </div>
              <hr role="separator" aria-orientation="horizontal" className="v-divider theme--dark" />
              <HeroData
                id={reportDetails.reportId || 'preview'}
                moduleName="ReportHero"
                props={{
                  reportId: reportId || '',
                  reportDonwloadLink: reportDetails.link?.href || '',
                  pdfLink: reportDetails.pdfLink || null,
                  intro: reportDetails.description,
                  title: reportDetails.reportTitle,
                  publishedDate: new Date(),
                  readTime: '0',
                  authors: [],
                  heroType: 'No Banner',
                  heroImage: { desktopImage: null, mobileImage: null },
                  heroEmbedVideo: '',
                  heroImageAlignment: 'Left',
                  heroBackGroundColour: '#fff',
                  thumbnailImage: null,
                  directory: null,
                  heroTextColour: 'Dark',
                  anchorTextColour: 'Black',
                  audioDescription: false,
                  ...reportDetails,
                }}
                hideAiSummaryBtn
                hideAudioPlayer
                hideGalleryButton
                hideDownloadHubButton
                hideSaveToScrapbookButton
              />

              <AtAGlance
                {...{
                  backgroundColour: '#fff',
                  mapModule: reportDetails.region,
                  ...reportDetails,
                }}
              />
            </div>
          ) : null}
        </div>

        {/* Footer Actions */}
        {reportDetails && (
          <div className="v-card__actions flex-shrink-0 justify-between rounded-b-lg bg-gray-100 py-5">
            <div className="spacer flex-1"></div>
            <button
              className="v-btn view-btn float-right mr-[19px] flex min-h-[44px] items-center rounded-4xl border border-black/20 px-7 py-2 !font-semibold tracking-[1.25px] !text-black/80 hover:bg-black/5"
              onClick={handleViewReport}
            >
              <div>{t('report.$viewReport')}</div>
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReportPreview;
